import { CustomButton } from "@/components/ui-elements/CustomButton";
import type { CustomTheme } from "@/theme/theme";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { FC, ReactNode } from "react";

interface CustomDialogProps {
  title: string;
  description?: string;
  open: boolean;
  onClose: () => void;
  primaryButtonText?: string;
  primaryButtonAction?: () => void;
  primaryButtonDisabled?: boolean;
  secondaryButtonText?: string;
  secondaryButtonAction?: () => void;
  children?: ReactNode;
  isLoading?: boolean;
}

export const CustomDialog: FC<CustomDialogProps> = ({
  title,
  description,
  open,
  onClose,
  primaryButtonText = "次へ",
  primaryButtonAction,
  primaryButtonDisabled = false,
  secondaryButtonText = "キャンセル",
  secondaryButtonAction,
  children,
  isLoading = false,
}) => {
  const theme: CustomTheme = useTheme();

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="custom-dialog-title"
        PaperProps={{
          style: {
            minWidth: "600px",
            borderRadius: "10px",
          },
          className: "styled-scrollbar-popup",
        }}
      >
        <Box sx={{ bgcolor: theme.palette.background.default }}>
          <DialogTitle id="custom-dialog-title">
            <Typography
              align="left"
              sx={{
                fontSize: theme.typography.subtitle2.fontSize,
                fontWeight: "bold",
              }}
            >
              {title}
            </Typography>
          </DialogTitle>
        </Box>
        <DialogContent className="styled-scrollbar-popup">
          {description && (
            <Typography
              component="p"
              align="left"
              sx={{
                fontSize: theme.typography.subtitle1.fontSize,
                paddingBottom: "20px",
              }}
              dangerouslySetInnerHTML={{ __html: description || "" }}
            />
          )}
          {children}
          <Box display="flex" justifyContent="end" sx={{ marginTop: "40px" }}>
            {secondaryButtonText && (
              <CustomButton
                label={secondaryButtonText}
                onClick={secondaryButtonAction}
                variant="cancel"
                size="sm"
                sx={{ marginRight: "10px" }}
                disabled={isLoading}
              />
            )}
            {primaryButtonText && (
              <CustomButton
                label={primaryButtonText}
                onClick={primaryButtonAction}
                disabled={primaryButtonDisabled || isLoading}
                variant="primary"
                size="sm"
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
